import { Component, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../../../core/services/auth.service';
import { UserListStore } from '../../services/userlist.store';
import { UserFilter } from '../../models/userlist.model';

@Component({
  selector: 'app-userlist',
  standalone: true,
  imports: [DatePipe, ReactiveFormsModule],
  templateUrl: './userlist.component.html',
  styleUrl: './userlist.component.scss'
})
export class UserListComponent {
  readonly store = inject(UserListStore);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);

  readonly filterTabs: { value: UserFilter; label: string }[] = [
    { value: 'all', label: 'Todos' },
    { value: 'verified', label: 'Verificados' },
    { value: 'unverified', label: 'No verificados' }
  ];

  readonly pageSizeOptions = [50, 100];

  // DNI: solo dígitos, sin puntos, 7 a 9 caracteres. Email: usuario@dominio.ext
  readonly busquedaForm = this.fb.nonNullable.group({
    dni: ['', Validators.pattern(/^\d{7,9}$/)],
    email: ['', Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)]
  });

  submitted = false;
  mostrarHintDni = false;
  mostrarErrorCaracterDni = false;

  constructor() {
    this.store.reiniciar();
  }

  // Sin usuarios en la respuesta (y ya terminó de cargar): no hay nada para exportar.
  get sinResultados(): boolean {
    return !this.store.cargando() && this.store.datos().users.length === 0;
  }

  logout(): void {
    this.auth.logout();
    this.router.navigate(['/login']);
  }

  buscar(): void {
    this.submitted = true;
    if (this.busquedaForm.invalid) return;
    this.store.buscar(this.busquedaForm.getRawValue());
    this.busquedaForm.reset();
    this.submitted = false;
  }

  limpiarFiltros(): void {
    this.busquedaForm.reset();
    this.submitted = false;
    this.store.limpiarBusqueda();
  }

  esInvalido(campo: 'dni' | 'email'): boolean {
    const ctrl = this.busquedaForm.controls[campo];
    return ctrl.invalid && this.submitted;
  }

  onFocusDni(): void {
    this.mostrarHintDni = !this.busquedaForm.controls.dni.value;
  }

  // El DNI solo acepta dígitos al tipear: una letra o símbolo no se escribe y
  // muestra un error, que se oculta al ingresar un número o al salir del campo.
  // El pegado lo resuelve onPegarDni (limpia en vez de rechazar).
  onAntesDeEscribirDni(event: InputEvent): void {
    if (event.inputType !== 'insertText' || !event.data) return;
    this.mostrarHintDni = false;
    if (/\D/.test(event.data)) {
      event.preventDefault();
      this.mostrarErrorCaracterDni = true;
    } else {
      this.mostrarErrorCaracterDni = false;
    }
  }

  onBlurDni(): void {
    this.mostrarHintDni = false;
    this.mostrarErrorCaracterDni = false;
  }

  // Al pegar un DNI con puntos/espacios/guiones (ej. 43.812.770) se dejan solo
  // los dígitos. Se intercepta el paste porque el maxlength="9" del input
  // cortaría el texto con puntos antes de poder limpiarlo.
  onPegarDni(event: ClipboardEvent): void {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const digitos = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '');
    const inicio = input.selectionStart ?? input.value.length;
    const fin = input.selectionEnd ?? input.value.length;
    const valor = (input.value.slice(0, inicio) + digitos + input.value.slice(fin)).slice(0, 9);
    this.busquedaForm.controls.dni.setValue(valor);
    this.mostrarHintDni = false;
    this.mostrarErrorCaracterDni = false;
  }

  onCambiarTamanoPagina(size: string): void {
    this.store.cambiarTamanoPagina(Number(size));
  }

  previousPage(): void {
    const f = this.store.filtros();
    if (f.page > 0) this.store.cambiarPagina(f.page - 1);
  }

  nextPage(): void {
    const f = this.store.filtros();
    const d = this.store.datos();
    if (f.page < d.totalPages - 1) this.store.cambiarPagina(f.page + 1);
  }
}
